import { useEffect, useRef, useState } from "react";
import { useDebounce } from "hooks/useDebounce";
import { fetchCollectionSearchApi } from "hooks/collection";
import { fetchShowSearchApi } from "hooks/shows-master";

type SearchItemType = 'collection' | 'show';
interface Collection {
  id: number;
  name: string;
  thumbnail?: string
  type: SearchItemType; // Added for differentiation between collections and shows
}

interface Props {
  value?: Collection | null;
  onSelect: (category: Collection) => void;
  placeholder?: string;
  disabled?: boolean
}

export default function SearchableCollectionData({
  disabled,
  value,
  onSelect,
  placeholder = "Search collection & show...",
}: Props) {
  const [query, setQuery] = useState(value?.name || "");
  const [options, setOptions] = useState<Collection[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isUserTypingRef = useRef(false);

  const debouncedQuery = useDebounce(query, 400);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update query when value prop changes (for initialData/prefill)
  useEffect(() => {
    if (value?.name && !isUserTypingRef.current) {
      setQuery(value.name);
    } else if (!value && !isUserTypingRef.current) {
      setQuery("");
    }
  }, [value?.name, value?.id]);

  /* 🔍 Fetch categories */
  // useEffect(() => {
  //   if (!debouncedQuery.trim()) {
  //     setOptions([]);
  //     return;
  //   }

  //   const load = async () => {
  //     try {
  //       setLoading(true);
  //       const res = await fetchCollectionSearchApi(debouncedQuery);
  //       setOptions(res);
  //     } catch (err) {
  //       console.error(err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   load();
  // }, [debouncedQuery]);

  const fetchSearchData = async (search: string): Promise<Collection[]> => {
    const [collections, shows] = await Promise.all([
      fetchCollectionSearchApi(search),
      fetchShowSearchApi(search)
    ]);
    const normalizedCollections = collections.map((item: any) => ({
      ...item, // keep full API response
      type: 'collection' as const
    }));

    const normalizedShows = shows.map((item: any) => ({
      ...item,
      type: 'show' as const
    }));

    return [...normalizedCollections, ...normalizedShows];
  };

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setOptions([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchSearchData(debouncedQuery);
        setOptions(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (item: Collection) => {
    isUserTypingRef.current = false;
    onSelect(item);
    setQuery("")
    setOpen(false);
  };

  // Keep data split for section-wise UI rendering.
  const collections = options.filter(i => i.type === 'collection');
  const shows = options.filter(i => i.type === 'show');

  const renderOptionItem = (item: Collection) => (
    <div
      key={`${item.type}-${item.id}`}
      onClick={() => handleSelect(item)}
      className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700"
    >
      {item.thumbnail && (
        <img
          src={item.thumbnail || "/placeholder.png"}
          alt={item.name}
          className="h-6 w-10 rounded object-cover"
        />
      )}
      <span className="flex-1">{item.name}</span>
    </div>
  );

  const renderSection = (title: string, items: Collection[]) => {
    if (items.length === 0) return null;

    return (
      <div>
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-600">
          {title}
        </div>
        {items.map(renderOptionItem)}
      </div>
    );
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <input
        disabled={disabled}
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          isUserTypingRef.current = true;
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          // If value exists, allow user to search by clearing query
          if (value?.name && query === value.name) {
            isUserTypingRef.current = false;
          }
        }}
        onBlur={() => {
          // Reset typing flag after a delay to allow value updates
          setTimeout(() => {
            isUserTypingRef.current = false;
          }, 200);
        }}
        className={`
    w-full rounded-md border px-3 py-2 text-sm
    ${disabled
            ? 'bg-gray-100 cursor-not-allowed'
            : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }
  `}
      />

      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No results found
            </div>
          )}

          {!loading && options.length > 0 && (
            <>
              {renderSection("Collections", collections)}
              {collections.length > 0 && shows.length > 0 && (
                <div className="mx-3 border-b border-gray-100" />
              )}
              {renderSection("Shows", shows)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
